import { useEffect, useState } from "react";

/** COMPONENT */
import { Box, CircularProgress, Typography } from "@mui/material";
import ModalCustom from "../../../component/modalCustom/modalCustom";
import { notiError } from "../../../component/notification/notifications";
import StallTable from "../components/StallTable";

/** API */
import InheritanceTransferApi, {
  InheritanceTransferDetail,
} from "../../../api/inheritance-transfer/inheritance-transfer.api";

/** UTILS */
import { colors } from "../../../constants/colors";
import {
  inheritanceStatusBg,
  inheritanceStatusColor,
  inheritanceStatusLabel,
} from "../../../constants/inheritanceTransfer";
import { dateTimeToView } from "../../../utils/date.utils";
import { getApiErrorMessage } from "../../../utils/api-error.utils";

/** STYLE */
import * as Style from "../inheritanceTransfer.style";

type Props = {
  id: number;
  onClose: () => void;
};

export default function ModalInheritanceDetail({ id, onClose }: Props) {
  const [detail, setDetail] = useState<InheritanceTransferDetail | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    /** กัน setState หลัง unmount ตอนผู้ใช้ปิด modal ก่อน response กลับมา */
    let isMounted = true;

    const loadDetail = async () => {
      setIsLoading(true);
      try {
        const res = await InheritanceTransferApi.findById(id);
        if (!isMounted) return;
        setDetail(res.data?.data || null);
      } catch (error) {
        if (!isMounted) return;
        setDetail(null);
        notiError(getApiErrorMessage(error, "ดึงรายละเอียดคำร้องไม่สำเร็จ"));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadDetail();
    return () => {
      isMounted = false;
    };
  }, [id]);

  const renderRow = (label: string, value: React.ReactNode) => (
    <Style.DetailRow>
      <Typography sx={{ fontSize: 16, color: colors.textLightGray }}>{label}</Typography>
      <Box sx={{ fontSize: 16, wordBreak: "break-word" }}>{value}</Box>
    </Style.DetailRow>
  );

  /** ค่าที่ backend ส่ง null มาแสดงเป็นขีดตามดีไซน์ ไม่ใช่ช่องว่าง */
  const textOrDash = (value?: string | null) => value || "–";

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={32} sx={{ color: colors.themeMainColor }} />
        </Box>
      );
    }

    if (!detail) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 6 }}>
          <Typography sx={{ color: colors.textLightGray }}>ไม่พบคำร้องโอนมรดกที่ต้องการ</Typography>
        </Box>
      );
    }

    const stalls = detail.stalls || [];

    return (
      <Box className="custom-scroll" sx={{ px: { xs: 2, md: 3 }, pb: 1, maxHeight: "calc(100vh - 220px)", overflowY: "auto" }}>
        <Box sx={{ border: `1px solid ${colors.lightGray}`, borderRadius: "8px", backgroundColor: colors.white }}>
          {renderRow("เลขที่คำร้อง", textOrDash(detail.inheritance_no))}
          {renderRow("ผู้ถือสิทธิเดิม", textOrDash(detail.owner_fullname))}
          {renderRow("ผู้รับโอน", textOrDash(detail.heir_fullname))}
          {renderRow("ความสัมพันธ์", textOrDash(detail.relationship_name))}
          {renderRow(
            "วันเวลาที่เพิ่มคำร้อง",
            detail.request_date ? dateTimeToView(detail.request_date) : "–"
          )}
          {renderRow(
            "วันเวลาที่พิจารณา",
            detail.reviewed_date ? dateTimeToView(detail.reviewed_date) : "–"
          )}
          {renderRow("ผู้พิจารณา", textOrDash(detail.reviewer_name))}
          {renderRow(
            "สถานะคำร้อง",
            <Style.StatusChip
              chipColor={inheritanceStatusColor(detail.status)}
              chipBg={inheritanceStatusBg(detail.status)}
            >
              {inheritanceStatusLabel(detail.status)}
            </Style.StatusChip>
          )}
          {/* ผลการพิจารณาโชว์เฉพาะเมื่อมีข้อความจริง แถวว่างเปล่าไม่ได้ให้ข้อมูลอะไร */}
          {detail.review_note ? renderRow("รายละเอียดการพิจารณา", detail.review_note) : null}
        </Box>

        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 3, mb: 1, flexWrap: "wrap" }}>
          <Style.SectionTitle>ข้อมูลแผงค้าของผู้โอน</Style.SectionTitle>
          <Typography sx={{ fontSize: 16, color: colors.textLightGray }}>
            {`${stalls.length} แผงค้า · ${detail.owner_fullname || "-"}`}
          </Typography>
        </Box>

        {/* snapshot ตอนยื่นคำร้อง ค่าอาจต่างจากสัญญาปัจจุบันได้ ถือเป็นเรื่องปกติ */}
        <StallTable stalls={stalls} textEmptyData="ไม่พบข้อมูลแผงค้าในคำร้องนี้" />
      </Box>
    );
  };

  return (
    <ModalCustom
      // ต้องเป็น true เสมอ + ให้ parent conditional render (ค่า false จะ fall back ไปอ่าน redux)
      isShowProps
      // ปิด modal เองผ่าน onClose ไม่ให้ไป reset redux modal.isShow ของ modal อื่นในหน้าเดียวกัน
      autoResetModal={false}
      size="xl"
      closeButton
      title="รายละเอียดคำร้องโอนมรดก"
      component={renderBody()}
      // ไม่ส่ง onSubmit/textBtnConfirm footer จึงไม่มีปุ่ม เหลือแค่ X มุมขวาบนตามดีไซน์
      onClose={onClose}
    />
  );
}
